import React, { useState, useMemo } from 'react';
import './CommodityCard.css';
import 'swiper/css';
import config from '../../../config';
import { FiPackage } from "react-icons/fi";

const toPersianNumber = (n) => {
    const farsiDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    if (!n && n !== 0) return '';
    return n
        .toString()
        .replace(/\d/g, (x) => farsiDigits[x]);
};

function CommodityCard({ commodity, onAdd, Card, badge, onOpenModal }) {

    const [count, setCount] = useState(0);
    const [imgError, setImgError] = useState(false);

    if (Card != null && count === 0) {
        setCount(Card.quantity);
    }

    const priceInfo = useMemo(() => {
        if (!commodity || !commodity.properties) return null;

        try {
            const parsedData = typeof commodity.properties === 'string'
                ? JSON.parse(commodity.properties)
                : commodity.properties;

            let variants = [];

            if (parsedData?.propertyData) {
                Object.values(parsedData.propertyData).forEach(varArray => {
                    if (Array.isArray(varArray)) {
                        variants = variants.concat(varArray.filter(v => 
                            v.active === true || 
                            v.active === 'true' || 
                            v.active === undefined
                        ));
                    }
                });
            }
            else if (Array.isArray(parsedData) && parsedData.length > 0) {
                variants = parsedData.filter(v => 
                    v.active === true || 
                    v.active === 'true' || 
                    v.active === undefined
                );
            }

            // فیلتر کردن متغیرهایی که قیمت صفر دارند تا باعث خطای "از ۰ تا X" نشوند
            const variantsWithPrice = variants.filter(v => parseInt(v.rawPrice || v.price || 0) > 0);
            const finalVariants = variantsWithPrice.length > 0 ? variantsWithPrice : variants;

            if (finalVariants.length === 0) return null;

            const prices = finalVariants.map(v => parseInt(v.rawPrice || v.price || 0));
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const discountPercent = parseFloat(commodity.discount_percent || 0);
            
            const totalStock = finalVariants.reduce((sum, v) => sum + parseInt(v.stock || 0), 0);
            
            // بررسی اینکه آیا این کالا بیش از یک متغیر (ویژگی) معتبر دارد یا خیر
            const hasMultipleVariants = finalVariants.length > 1;

            if (!hasMultipleVariants || minPrice === maxPrice) {
                const originalPrice = minPrice;
                if (originalPrice === 0 && totalStock === 0) return null;

                const finalPrice = originalPrice - ((originalPrice * discountPercent) / 100);

                return {
                    isRange: false,
                    original: originalPrice,
                    final: Math.round(finalPrice),
                    hasDiscount: discountPercent > 0,
                    stock: totalStock,
                    hasMultipleVariants
                };
            }

            return {
                isRange: true,
                min: minPrice,
                max: maxPrice,
                hasDiscount: discountPercent > 0,
                stock: totalStock,
                hasMultipleVariants
            };

        } catch (error) {
            console.error("Error parsing price in CommodityCard:", error);
            return null;
        }
    }, [commodity]);

    return (
        <div className="commodity-card" onClick={(e) => {
            e.stopPropagation();
            if (onOpenModal) onOpenModal();
        }}>
            <div className="commodity-right">
                {commodity.image_path && !imgError ? (
                    <img
                        src={`${config.api.imageURL}/${commodity.image_path}`}
                        alt={"کالا"}
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f3f4f6',
                        borderTopRightRadius: '10px',
                        borderBottomRightRadius: '10px',
                        color: '#9ca3af'
                    }}>
                        <FiPackage size={40} strokeWidth={1.5} />
                    </div>
                )}
            </div>

            <div className="commodity-left">
                <div>
                    <div className="commodity-title">
                        {toPersianNumber((commodity.title ?? '').substring(0, 50))}
                    </div>
                    <div className="commodity-description">
                        {toPersianNumber((commodity.extraTitle ?? '').substring(0, 50))}
                    </div>
                </div>

                {priceInfo && priceInfo.hasDiscount && (
                    <div className="discount">
                        <div>{toPersianNumber(Math.round(commodity.discount_percent, 0))}%</div>
                        <div style={{ fontSize: '8px' }}>تخفیف</div>
                    </div>
                )}

                <div className="price-container">
                    {priceInfo ? (
                        priceInfo.isRange ? (
                            <>
                                <div className="price-discount" style={{ textDecoration: 'none' }}>
                                    از {toPersianNumber(priceInfo.min.toLocaleString())}
                                </div>
                                <div className="commodity-price">
                                    <span>تا {toPersianNumber(priceInfo.max.toLocaleString())}</span>
                                    <span style={{ fontSize: "11px", marginRight: '2px' }}>تومان</span>
                                </div>
                                {/* اگر کالا ناموجود باشد کلمه ناموجود چاپ می‌شود، اما اگر موجود باشد در حالت محدوده کلمه "موجود" چاپ نمی‌شود */}
                                {priceInfo.stock <= 0 && (
                                    <div className="stock-status" style={{ color: "red", fontSize: '11px', marginTop: '4px' }}>
                                        ناموجود
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {priceInfo.hasDiscount && (
                                    <div className="price-discount">
                                        {toPersianNumber(priceInfo.original.toLocaleString())}
                                    </div>
                                )}
                                <div className="commodity-price">
                                    <span>{toPersianNumber(priceInfo.final.toLocaleString())}</span>
                                    <span style={{ fontSize: "11px", marginRight: '2px' }}>تومان</span>
                                </div>
                                <div className="stock-status" style={{ color: priceInfo.stock > 0 ? "green" : "red" }}>
                                    {priceInfo.stock > 0
                                        ? (priceInfo.hasMultipleVariants ? "موجود" : `موجود: ${toPersianNumber(priceInfo.stock)}`)
                                        : "ناموجود"}
                                </div>
                            </>
                        )
                    ) : (
                        <div className="commodity-price" style={{ color: '#999' }}>
                            ناموجود
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CommodityCard;
